/** What Customize offers to add back (Home spec §4): every widget the
 * viewer can see and does not already have on the page (`homeLayout.addable`),
 * under one heading per non-empty group (SA-1332, spec §4.7). */
import { useId } from 'react'
import { Button } from '@/components/ui'
import type { HomeWidgetEntry } from '@/components/custom/homeWidgetTypes'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { GROUP_ORDER, GROUP_TITLES } from './homeWords'

const LABEL = 'text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground'

export function AddWidgetList({ widgets, onAdd }: { widgets: HomeWidgetEntry[]; onAdd: (widget: HomeWidgetEntry) => void }) {
  const base = useId()
  const groups = GROUP_ORDER.map((g) => ({ group: g, items: widgets.filter((w) => w.group === g) })).filter((g) => g.items.length > 0)
  return (
    <section {...tourAnchor('home.customize.add')} role="region" aria-label="Add a widget" tabIndex={-1} className="rounded-lg border border-dashed border-border px-4 py-3.5 outline-none">
      <span className={LABEL}>Add a widget</span>
      {widgets.length === 0 ? <p className="mt-2 text-[12.5px] text-muted-foreground">Everything you can see is already on the page.</p> : (
        groups.map(({ group, items }) => (
          <div key={group} role="group" aria-labelledby={`${base}-${group}`} className="mt-3">
            <span id={`${base}-${group}`} className={LABEL}>{GROUP_TITLES[group]}</span>
            <ul className="mt-2.5 grid list-none grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-2 p-0">
              {items.map((w) => (
                <li key={w.id} className="flex items-start gap-2.5 rounded-md border border-border bg-card px-3 py-2.5 text-[12.5px] text-muted-foreground">
                  <span><b className="block text-[13px] text-foreground">{w.title}</b>{w.blurb}</span>
                  <Button variant="outline" size="sm" className="ml-auto shrink-0" aria-label={`Add ${w.title}`} onClick={() => onAdd(w)}>Add</Button>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  )
}
